import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getNodeApiUrl } from '../config/api.config';

/**
 * Componente Select2 riutilizzabile per tutto il progetto
 * Supporta sia opzioni statiche che caricamento dinamico tramite AJAX
 */
const Select2 = ({
  id,
  placeholder = 'Seleziona...',
  value = null,
  options = [],
  onChange = () => {},
  ajaxUrl = null,
  ajaxParams = {},
  multiple = false,
  allowClear = false,
  disabled = false,
  minimumInputLength = 0,
  className = ''
}) => {
  const selectRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Aggiungiamo stili personalizzati per mantenere la coerenza visiva
    const styleId = 'select2-custom-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .select2-container--default .select2-selection--single {
          height: 38px;
          border-radius: 4px;
        }
        .select2-container--default .select2-selection--single .select2-selection__arrow {
          height: 36px;
        }
        .select2-container .select2-selection--single .select2-selection__rendered {
          padding-left: 12px;
          line-height: 36px;
        }
        .select2-container--default .select2-selection--single {
          border: 1px solid #ced4da;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      if (!document.body.classList.contains('app')) {
        const styleElem = document.getElementById(styleId);
        if (styleElem) styleElem.remove();
      }
    };
  }, []);

  // Configurazione dell'header di autenticazione per tutte le richieste AJAX
  useEffect(() => {
    // Configura l'intercettore AJAX per aggiungere il token a tutte le richieste
    const token = localStorage.getItem('installamelo-auth-token');
    if (token && window.$) {
      window.$(document).ajaxSend(function(event, xhr) {
        xhr.setRequestHeader('auth-token', token);
      });
    }
  }, []);
  
  // Inizializzazione e gestione di Select2
  useEffect(() => {
    if (!selectRef.current) return;

    try {
      // Per evitare reinizializzazioni multiple, distruggiamo l'istanza precedente se esiste
      if (window.$(selectRef.current).hasClass('select2-hidden-accessible')) {
        window.$(selectRef.current).select2('destroy');
      }

      let select2Config = {
        placeholder,
        allowClear,
        width: '100%'
      };

        if (multiple) {
          select2Config.multiple = true;
        }

        // Configura opzioni per caricamento AJAX
        if (ajaxUrl) {
          select2Config.minimumInputLength = minimumInputLength;
          select2Config.ajax = {
            url: () => {
              // Utilizziamo la funzione getNodeApiUrl per costruire l'URL completo
              return getNodeApiUrl(ajaxUrl);
            },
            dataType: 'json',
            delay: 250,
            data: (params) => {
              return {
                searchTerm: params.term || '',
                page: params.page || 1,
                ...ajaxParams
              };
            },
            processResults: (data, params) => {
              params.page = params.page || 1;
              return {
                results: data.items || data,
                pagination: {
                  more: (params.page * 30) < data.total_count
                }
              };
            },
            cache: true
          };
        } else if (options && options.length > 0) {
          // Usa opzioni statiche se fornite
          select2Config.data = options;
        }

      // Inizializza Select2
      window.$(selectRef.current).select2(select2Config);

      // Gestisce evento di cambiamento
      window.$(selectRef.current).on('change', function() {
        const selectedValue = window.$(this).val();
        const selectedData = window.$(this).select2('data');
        const selected = multiple ? 
          selectedData.map(item => ({ id: item.id, text: item.text })) : 
          (selectedData[0] ? { id: selectedData[0].id, text: selectedData[0].text } : null);
        
        onChange(selected);
      });

      // Se viene passato un valore, imposta il valore selezionato
      if (value !== null && selectRef.current) {
        if (multiple && Array.isArray(value)) {
          window.$(selectRef.current).val(value.map(v => v.id || v));
        } else if (!multiple && value) {
          window.$(selectRef.current).val(value.id || value);
        } else {
          window.$(selectRef.current).val(null);
        }
        
        window.$(selectRef.current).trigger('change');
      }

      // Aggiorna lo stato disabled
      window.$(selectRef.current).prop('disabled', disabled);

    } catch (err) {
      console.error("Errore nell'inizializzazione di Select2:", err);
      setError(`Errore nell'inizializzazione di Select2: ${err.message}`);
    }

    // Cleanup
    return () => {
      try {
        // Controlla se l'elemento esiste e se select2 è stato inizializzato su di esso
        if (selectRef.current && window.$(selectRef.current).hasClass('select2-hidden-accessible')) {
          window.$(selectRef.current).select2('destroy');
        }
      } catch (err) {
        // Non è un errore critico se il cleanup fallisce, ma lo logghiamo
        console.warn("Attenzione nel cleanup di Select2:", err);
      }
    };
  }, [placeholder, options, value, multiple, allowClear, disabled, ajaxUrl, minimumInputLength, ajaxParams]);

  // Fallback in caso di errore
  if (error) {
    return (
      <select
        id={id}
        className={`form-control ${className}`}
        style={{ width: '100%' }}
        disabled={disabled}
        onChange={(e) => {
          const selectedOption = options.find(opt => opt.id === e.target.value);
          onChange(selectedOption || null);
        }}
        value={value ? (value.id || value) : ''}
      >
        <option value="">{placeholder}</option>
        {options && options.map(option => (
          <option key={option.id} value={option.id}>
            {option.text}
          </option>
        ))}
      </select>
    );
  }

  return (
    <select
      id={id}
      ref={selectRef}
      className={`form-control ${className}`}
      style={{ width: '100%' }}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {options && options.map(option => (
        <option key={option.id} value={option.id}>
          {option.text}
        </option>
      ))}
    </select>
  );
};

Select2.propTypes = {
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func,
  ajaxUrl: PropTypes.string,
  ajaxParams: PropTypes.object,
  multiple: PropTypes.bool,
  allowClear: PropTypes.bool,
  disabled: PropTypes.bool,
  minimumInputLength: PropTypes.number,
  className: PropTypes.string
};

export default Select2;
